import {zodResolver} from '@hookform/resolvers/zod';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Image} from 'src/design/atoms/Image';
import {Stack} from 'src/design/atoms/Stack';
import React, {useEffect, useState} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {z} from 'zod';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import type {InstanceSettingsResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {Description} from 'src/design/atoms/Description';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Spinner} from 'src/design/atoms/Spinner';
import {Textbox} from 'src/design/atoms/Textbox';
import {ViewField} from 'src/design/atoms/ViewField';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {
  useGetInstanceSettingsQuery,
  useUpdateInstanceSettingsMutation,
} from 'src/redux/queries/moduleExtension';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useInstanceLogoUrl} from 'src/shared/hooks/useInstanceLogoUrl';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

const ADDRESS_MAX = 1000;
const FIELD_MAX = 255;

const settingsSchema = z.object({
  letterheadAddress: z
    .string()
    .trim()
    .max(ADDRESS_MAX, `Address must be ${ADDRESS_MAX} characters or fewer`),
  letterheadPhone: z
    .string()
    .trim()
    .max(FIELD_MAX, `Phone must be ${FIELD_MAX} characters or fewer`),
  letterheadEmail: z
    .string()
    .trim()
    .max(FIELD_MAX, `Email must be ${FIELD_MAX} characters or fewer`)
    .refine(v => v === '' || z.string().email().safeParse(v).success, {
      message: 'Enter a valid email address',
    }),
  letterheadWebsite: z
    .string()
    .trim()
    .max(FIELD_MAX, `Website must be ${FIELD_MAX} characters or fewer`),
});

type SettingsFormValues = z.infer<typeof settingsSchema>;

const toFormValues = (
  data: InstanceSettingsResponse | undefined
): SettingsFormValues => ({
  letterheadAddress: data?.letterheadAddress ?? '',
  letterheadPhone: data?.letterheadPhone ?? '',
  letterheadEmail: data?.letterheadEmail ?? '',
  letterheadWebsite: data?.letterheadWebsite ?? '',
});

export const InstanceSettingsTab: React.FC = () => {
  const {setAlert} = useAlert();
  const [isEditing, setIsEditing] = useState(false);

  const {
    data: settings,
    isLoading,
    isError,
  } = useGetInstanceSettingsQuery();

  const [updateSettings, {isLoading: isSaving}] =
    useUpdateInstanceSettingsMutation();

  const {hasPermission: canUpdate} = useHasModulePermission({
    module: ModuleEnum.ModuleExtensions,
    operation: PermissionOperationEnum.Update,
  });

  const {control, handleSubmit, reset} = useForm<SettingsFormValues>({
    defaultValues: toFormValues(undefined),
    resolver: zodResolver(settingsSchema),
  });

  useEffect(() => {
    if (settings) reset(toFormValues(settings));
  }, [settings, reset]);

  const addressValue = useWatch({control, name: 'letterheadAddress'}) ?? '';
  const logoUrl = useInstanceLogoUrl();

  if (isLoading) {
    return <Spinner loadingText="Loading settings..." />;
  }

  if (isError || !settings) {
    return (
      <ErrorAlert message="Instance settings could not be loaded. Please try again." />
    );
  }

  const handleEdit = () => {
    reset(toFormValues(settings));
    setIsEditing(true);
  };

  const handleCancel = () => {
    reset(toFormValues(settings));
    setIsEditing(false);
  };

  const handleSave = handleSubmit(async values => {
    try {
      await updateSettings({
        letterheadAddress: values.letterheadAddress || undefined,
        letterheadPhone: values.letterheadPhone || undefined,
        letterheadEmail: values.letterheadEmail || undefined,
        letterheadWebsite: values.letterheadWebsite || undefined,
      }).unwrap();
      setAlert('Letterhead settings saved', 'success');
      setIsEditing(false);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  });

  const editAction =
    !isEditing && canUpdate ? (
      <Button size="sm" variant="subtle" onClick={handleEdit}>
        Edit
      </Button>
    ) : undefined;

  return (
    <SectionCard title="Letterhead" rightSection={editAction}>
      <Box p="md">
        <Stack gap="md">
          <Description
            size="sm"
            mt={0}
            text="These details render on generated inspection documents in the letterhead contact block."
          />

          <Group gap="md" align="center">
            {logoUrl && (
              <Image
                src={logoUrl}
                alt={`${settings.name} logo`}
                h={40}
                w="auto"
                fit="contain"
              />
            )}
            <ViewField label="Company" value={settings.name} />
          </Group>
          <Description
            mt={0}
            text="Company name and logo are managed by the platform team and cannot be changed here."
          />

          <Divider />

          {isEditing ? (
            <>
              <Controller
                name="letterheadAddress"
                control={control}
                render={({field, fieldState}) => (
                  <Textbox
                    multiline
                    name={field.name}
                    value={field.value}
                    onChangeCallback={field.onChange}
                    onBlurCallback={field.onBlur}
                    label="Address"
                    placeholder={'123 Safety Way, Suite 400\nPortland, OR 97201'}
                    minRows={3}
                    maxLength={ADDRESS_MAX}
                    helperText={`${addressValue.length} / ${ADDRESS_MAX.toLocaleString()}`}
                    error={!!fieldState.error}
                    errorText={fieldState.error?.message}
                    props={{autosize: true}}
                  />
                )}
              />
              <Grid gutter="md">
                <Grid.Col span={{base: 12, md: 6}}>
                  <Controller
                    name="letterheadPhone"
                    control={control}
                    render={({field, fieldState}) => (
                      <Textbox
                        name={field.name}
                        value={field.value}
                        onChangeCallback={field.onChange}
                        onBlurCallback={field.onBlur}
                        label="Phone"
                        placeholder="(555) 123-4567"
                        maxLength={FIELD_MAX}
                        error={!!fieldState.error}
                        errorText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid.Col>
                <Grid.Col span={{base: 12, md: 6}}>
                  <Controller
                    name="letterheadEmail"
                    control={control}
                    render={({field, fieldState}) => (
                      <Textbox
                        name={field.name}
                        value={field.value}
                        onChangeCallback={field.onChange}
                        onBlurCallback={field.onBlur}
                        label="Email"
                        placeholder="safety@yourcompany.com"
                        maxLength={FIELD_MAX}
                        error={!!fieldState.error}
                        errorText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid.Col>
                <Grid.Col span={{base: 12, md: 6}}>
                  <Controller
                    name="letterheadWebsite"
                    control={control}
                    render={({field, fieldState}) => (
                      <Textbox
                        name={field.name}
                        value={field.value}
                        onChangeCallback={field.onChange}
                        onBlurCallback={field.onBlur}
                        label="Website"
                        placeholder="www.yourcompany.com"
                        maxLength={FIELD_MAX}
                        error={!!fieldState.error}
                        errorText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid.Col>
              </Grid>
              <Group>
                <Button variant="subtle" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleSave} loading={isSaving}>
                  Save
                </Button>
              </Group>
            </>
          ) : (
            <Grid gutter="md">
              <Grid.Col span={12}>
                <ViewField
                  label="Address"
                  value={
                    settings.letterheadAddress ? (
                      <span style={{whiteSpace: 'pre-line'}}>
                        {settings.letterheadAddress}
                      </span>
                    ) : (
                      '—'
                    )
                  }
                />
              </Grid.Col>
              <Grid.Col span={{base: 12, md: 6}}>
                <ViewField label="Phone" value={settings.letterheadPhone} />
              </Grid.Col>
              <Grid.Col span={{base: 12, md: 6}}>
                <ViewField label="Email" value={settings.letterheadEmail} />
              </Grid.Col>
              <Grid.Col span={{base: 12, md: 6}}>
                <ViewField label="Website" value={settings.letterheadWebsite} />
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </Box>
    </SectionCard>
  );
};
